import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Crosshair, Download, Lock, Minimize2, PencilRuler, PlusCircle, Unlock,
} from 'lucide-react';
import { VIEW_PERSON, VIEW_TREE } from '../orgChartModel';

// Araç çubuğu (spec 2026-10-24 §4; sayfa gövdesinde başlık YOK — UI altın kuralı): [Ağaç | Kişi Odaklı] · arama · Beni Bul · Hepsini Daralt · Dışa Aktar ·
// Düzenle (EM.Write) · düzenle kipinde kilit + kök ekle · sağda departman lejantı. Kişi Odaklı'da yalnız ağaçla ilgili
// kontroller (Hepsini Daralt, Dışa Aktar, Düzenle) gizlenir. Yakınlaştırma düğmeleri tuvalin sağ altındadır (Controls).

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #E6EAF0;
  font-family: 'Nunito Sans', sans-serif;
`;

const Seg = styled.div`
  display: inline-flex;
  background: #EEF2F6;
  border-radius: 10px;
  padding: 3px;
`;

const SegBtn = styled.button`
  border: 0;
  padding: 7px 14px;
  border-radius: 8px;
  font: 600 13px 'Nunito Sans', sans-serif;
  cursor: pointer;
  background: ${({ $on }) => ($on ? '#fff' : 'transparent')};
  color: ${({ $on, theme }) => ($on ? '#1F2A37' : theme.colors.text.muted)};
  box-shadow: ${({ $on }) => ($on ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};
`;

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#E6EAF0')};
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.08)' : '#fff')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#1F2A37')};
  font: 600 13px 'Nunito Sans', sans-serif;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: #CBD5E1; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Spacer = styled.div`
  flex: 1;
`;

const MenuWrap = styled.div`
  position: relative;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  min-width: 220px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #E6EAF0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
  padding: 4px;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  text-align: left;
  border: none;
  background: transparent;
  padding: 8px 10px;
  border-radius: 6px;
  font: 13px 'Nunito Sans', sans-serif;
  color: #1F2A37;
  cursor: pointer;
  &:hover:not(:disabled) { background: #F5F7FA; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

function useOutsideClose(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, setOpen]);
  return ref;
}

export default function Toolbar({
  view = VIEW_TREE, onViewChange, searchSlot, ownPositions = [], onMe, onCollapseAll, onExport, exporting = false, canExportBranch = false,
  canEdit = false, editMode = false, onToggleEdit, locked = false, setLocked, onAddRoot, legendSlot,
}) {
  const { t } = useTranslation();
  const [meOpen, setMeOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const meRef = useOutsideClose(meOpen, setMeOpen);
  const exportRef = useOutsideClose(exportOpen, setExportOpen);
  const isTree = view === VIEW_TREE;
  const exportItem = (format, scope, key) => (
    <MenuItem
      type="button"
      data-testid={`export-${format}-${scope}`}
      disabled={scope === 'branch' && !canExportBranch}
      onClick={() => {
        setExportOpen(false);
        onExport?.(format, scope);
      }}
    >
      {t(key)}
    </MenuItem>
  );

  return (
    <Bar data-testid="org-toolbar">
      <Seg role="tablist">
        <SegBtn type="button" role="tab" data-testid="view-tree" $on={isTree} aria-selected={isTree}
          onClick={() => onViewChange?.(VIEW_TREE)}>
          {t('orgChart.viewTree')}
        </SegBtn>
        <SegBtn type="button" role="tab" data-testid="view-person" $on={!isTree} aria-selected={!isTree}
          onClick={() => onViewChange?.(VIEW_PERSON)}>
          {t('orgChart.viewPerson')}
        </SegBtn>
      </Seg>
      {searchSlot}

      {ownPositions.length > 0 && (
        <MenuWrap ref={meRef}>
          <ToolBtn
            type="button"
            data-testid="tb-me"
            onClick={() => (ownPositions.length === 1 ? onMe?.(ownPositions[0].positionId) : setMeOpen((o) => !o))}
          >
            <Crosshair size={15} />
            {t('orgChart.findMe')}
          </ToolBtn>
          {meOpen && (
            <Menu role="menu">
              {ownPositions.map((own) => (
                <MenuItem
                  key={own.positionId}
                  type="button"
                  role="menuitem"
                  data-testid={`tb-me-${own.positionId}`}
                  onClick={() => {
                    setMeOpen(false);
                    onMe?.(own.positionId);
                  }}
                >
                  {[own.titleName, own.departmentName].filter(Boolean).join(' · ') || '-'}
                </MenuItem>
              ))}
            </Menu>
          )}
        </MenuWrap>
      )}

      {isTree && (
        <ToolBtn type="button" data-testid="tb-collapse-all" onClick={onCollapseAll}>
          <Minimize2 size={15} />
          {t('orgChart.collapseAll')}
        </ToolBtn>
      )}

      {isTree && (
        <MenuWrap ref={exportRef}>
          <ToolBtn type="button" data-testid="tb-export" disabled={exporting} aria-busy={exporting || undefined}
            onClick={() => setExportOpen((o) => !o)}>
            <Download size={15} />
            {t('orgChart.export')}
          </ToolBtn>
          {exportOpen && (
            <Menu role="menu">
              {exportItem('png', 'visible', 'orgChart.exportPngVisible')}
              {exportItem('pdf', 'visible', 'orgChart.exportPdfVisible')}
              {exportItem('png', 'branch', 'orgChart.exportPngBranchSelected')}
              {exportItem('pdf', 'branch', 'orgChart.exportPdfBranchSelected')}
            </Menu>
          )}
        </MenuWrap>
      )}

      {isTree && canEdit && (
        <ToolBtn type="button" data-testid="tb-edit" $active={editMode} aria-pressed={editMode} onClick={onToggleEdit}>
          <PencilRuler size={15} />
          {editMode ? t('orgChart.exitEdit') : t('orgChart.edit')}
        </ToolBtn>
      )}
      {isTree && editMode && (
        <>
          <ToolBtn type="button" data-testid="tb-lock" title={locked ? t('orgChart.unlock') : t('orgChart.lock')}
            onClick={() => setLocked?.((prev) => !prev)} $active={locked}>
            {locked ? <Lock size={15} /> : <Unlock size={15} />}
          </ToolBtn>
          <ToolBtn type="button" data-testid="tb-add-root" title={t('orgChart.addRoot')} onClick={onAddRoot}>
            <PlusCircle size={15} />
          </ToolBtn>
        </>
      )}

      <Spacer />
      {legendSlot}
    </Bar>
  );
}
